import { useState, useMemo } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Pencil, Check, X } from "lucide-react";
import { Button } from "~/atoms/button";
import { Input } from "~/atoms/input";
import { updateDocumentName } from "~/api/v2/requests";
import { toast } from "react-toastify";
import { FetchError, type DocumentDetails } from "~/api/types";
import { useDocumentVersions } from "~/api/v2/useDocumentVersions";
import { findDuplicateDocumentVersion } from "~/helpers/document";
import { cn } from "~/lib/utils";
import { UnsavedChangesAlert } from "~/molecules/UnsavedChangesAlert";

// Shared layout classes to prevent layout shift between view/edit modes
const CONTAINER_CLASSES = cn("flex flex-col");
const UNDERLINE_SPACER_CLASSES = cn("h-0.5 rounded-full");

function useDuplicateDocumentDetection({
  currentDocumentId,
  currentCategoryId,
  newTitle,
}: {
  currentDocumentId: number;
  currentCategoryId: number;
  newTitle: string;
}) {
  const { data: documentVersions } = useDocumentVersions();
  return useMemo(
    () =>
      findDuplicateDocumentVersion({
        documentVersions: documentVersions?.filter(
          (dv) => dv.document.id !== currentDocumentId,
        ),
        title: newTitle,
        categoryId: currentCategoryId,
      }),
    [documentVersions, newTitle, currentCategoryId, currentDocumentId],
  );
}

function DuplicateTitleWarning({
  duplicateDocumentVersion,
}: {
  duplicateDocumentVersion: {
    id: number;
    title: string;
    document: {
      id: number;
    };
  };
}) {
  const { t } = useTranslation();

  return (
    <p className="text-warning text-sm">
      {t(($) => $.modal.editDocumentTitle.duplicateWarning)}{" "}
      <Link
        to={`/documents/${duplicateDocumentVersion.document.id}?versionId=${duplicateDocumentVersion.id}&tab=metadata`}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-warning-foreground/60 dark:hover:text-warning/60 underline"
      >
        {duplicateDocumentVersion.title}
      </Link>
    </p>
  );
}

interface Props {
  document: DocumentDetails;
  onUpdate: () => void;
}

export function EditableDocumentTitle({ document, onUpdate }: Props) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [nameValue, setNameValue] = useState(document.name);
  const [isSaving, setIsSaving] = useState(false);
  const [isDocumentOpenInEditorError, setIsDocumentOpenInEditorError] =
    useState(false);

  const { mutate: mutateDocumentVersions } = useDocumentVersions();

  const duplicateDocumentVersion = useDuplicateDocumentDetection({
    currentDocumentId: document.id,
    currentCategoryId: document.category.id,
    newTitle: nameValue,
  });

  const handleStartEdit = () => {
    setNameValue(document.name);
    setIsDocumentOpenInEditorError(false);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setNameValue(document.name);
    setIsDocumentOpenInEditorError(false);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedName = nameValue.trim();

    // Same validation as in useDocumentEditForm: z.string().min(1)
    if (!trimmedName) {
      setIsDocumentOpenInEditorError(false);
      toast.error(t(($) => $.validations.required));
      return;
    }

    if (trimmedName === document.name) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setIsDocumentOpenInEditorError(false);
    try {
      // Same update call as DocumentMetadataPanel
      await updateDocumentName(document.id, trimmedName);
      onUpdate();
      void mutateDocumentVersions();

      setIsEditing(false);
      toast.success(t(($) => $.toasts.success.documentUpdated));
    } catch (error) {
      console.error("Error updating title:", error);
      if (
        error instanceof FetchError &&
        error.code === "DOCUMENT_OPEN_IN_EDITOR"
      ) {
        setIsDocumentOpenInEditorError(true);
      } else {
        toast.error(t(($) => $.toasts.error.generic));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      void handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const unsavedChangesAlert = (
    <UnsavedChangesAlert
      hasUnsavedChanges={isEditing}
      onLeave={handleCancel}
      title={t(($) => $.commons.unsavedChanges.documentTitle.title)}
      description={t(($) => $.commons.unsavedChanges.documentTitle.description)}
      cancelLabel={t(($) => $.commons.unsavedChanges.continueEditing)}
      confirmLabel={t(($) => $.commons.unsavedChanges.discardChanges)}
    />
  );

  if (!isEditing) {
    return (
      <>
        {unsavedChangesAlert}
        <div className={CONTAINER_CLASSES}>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-wrap wrap-break-word">
              {document.name}
            </h2>
            <Button
              size="icon"
              variant="ghost"
              onClick={handleStartEdit}
              className="size-8 shrink-0"
            >
              <Pencil className="size-4" />
            </Button>
          </div>
          <div className={UNDERLINE_SPACER_CLASSES} />
        </div>
      </>
    );
  }

  return (
    <>
      {unsavedChangesAlert}
      <div className={CONTAINER_CLASSES}>
        <div className="flex items-center gap-3">
          <div className="relative inline-block min-w-50">
            <span
              className="invisible text-2xl font-semibold whitespace-pre"
              aria-hidden="true"
            >
              {nameValue || t(($) => $.modal.editDocumentTitle.placeholder)}
            </span>
            <Input
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t(($) => $.modal.editDocumentTitle.placeholder)}
              disabled={isSaving}
              className="absolute top-0 left-0 h-auto w-full border-none bg-transparent p-0 text-2xl font-semibold outline-none focus-visible:ring-0 focus-visible:ring-offset-0 md:text-2xl"
              autoFocus
            />
          </div>
          <Button
            size="icon"
            variant="ghost"
            onClick={handleSave}
            disabled={isSaving || !nameValue.trim()}
            className="text-success hover:text-success size-8 shrink-0"
          >
            <Check className="size-5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={handleCancel}
            disabled={isSaving}
            className="text-muted-foreground hover:text-foreground size-8 shrink-0"
          >
            <X className="size-5" />
          </Button>
        </div>
        <div className={cn(UNDERLINE_SPACER_CLASSES, "bg-border")} />
        {isDocumentOpenInEditorError && (
          <p className="text-destructive text-sm">
            {t(($) => $.commons.workflow.documentOpenInEditorErrorDescription)}
          </p>
        )}
        {duplicateDocumentVersion && (
          <DuplicateTitleWarning
            duplicateDocumentVersion={duplicateDocumentVersion}
          />
        )}
      </div>
    </>
  );
}
